import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { ChevronDown, X } from "lucide-react";

import { useSyncConstraintValidationMessage } from "../../../hooks/use-sync-constraint-validation-message.ts";
import { useDismissOutside } from "../../../hooks/use-dismiss-outside.ts";
import { Dropdown } from "../dropdown/dropdown.tsx";

import styles from "./select.module.css";

export interface SelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

function isOptionDisabled(opt: SelectOption | undefined): boolean {
  return opt?.disabled === true;
}

export type SelectLabelLayout = "stacked" | "inline";

export type SelectDensity = "default" | "compact";

export interface SelectProps {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  id?: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  /** Synced to native constraint validation so form submits can show browser tooltips. */
  error?: string;
  /** Shows a clear (×) control on the field after a value is chosen. */
  isClearable?: boolean;
  /** `stacked` = label above field (default). `inline` = label left, field right (one row). */
  labelLayout?: SelectLabelLayout;
  /** Dense trigger (e.g. date picker header): smaller type and padding. */
  density?: SelectDensity;
  /**
   * Minimum width for the dropdown panel in pixels. When omitted, the panel widens
   * from the trigger width up to a width estimated from the longest option label.
   */
  panelMinWidth?: number;
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(
  function Select(
    {
      value,
      onChange,
      options = [],
      id: idProp,
      label,
      required = false,
      placeholder = "Select…",
      disabled = false,
      searchable = false,
      error,
      isClearable = false,
      labelLayout = "stacked",
      density = "default",
      panelMinWidth,
    },
    forwardedRef
  ) {
    const hasError = error != null && error !== "";

    const reactId = useId().replace(/:/g, "");
    const rootId = idProp ?? `select-${reactId}`;
    const listboxId = `${rootId}-listbox`;
    const triggerId = `${rootId}-trigger`;
    const errorId = `${rootId}-error`;

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const listboxRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const constraintValidationInputRef = useRef<HTMLInputElement | null>(null);

    const setWrapperRef = useCallback(
      (node: HTMLDivElement | null) => {
        wrapperRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef != null) {
          (forwardedRef as { current: HTMLDivElement | null }).current = node;
        }
      },
      [forwardedRef]
    );

    const [open, setOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");

    useSyncConstraintValidationMessage(constraintValidationInputRef, error);

    const onDismiss = useCallback(() => setOpen(false), []);

    useDismissOutside({
      ref: wrapperRef,
      isOpen: open,
      onDismiss,
      extraRefs: [panelRef],
    });

    const selected = useMemo(
      () => options.find((o) => o.value === value),
      [options, value]
    );

    const filteredOptions = useMemo(() => {
      if (!searchable || !searchQuery.trim()) return options;
      const q = searchQuery.toLowerCase();
      return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, searchQuery, searchable]);

    useEffect(() => {
      if (!open) {
        setSearchQuery("");
        return;
      }
      const idx = filteredOptions.findIndex(
        (o) => o.value === value && !isOptionDisabled(o),
      );
      if (idx >= 0) {
        setHighlightedIndex(idx);
        return;
      }
      const firstEnabled = filteredOptions.findIndex((o) => !isOptionDisabled(o));
      setHighlightedIndex(firstEnabled >= 0 ? firstEnabled : 0);
    }, [open, filteredOptions, value]);

    useEffect(() => {
      if (!open) return;
      const id = requestAnimationFrame(() => {
        if (searchable) {
          searchRef.current?.focus();
        } else {
          listboxRef.current?.focus();
        }
      });
      return () => cancelAnimationFrame(id);
    }, [open, searchable]);

    const prevOpen = useRef(open);
    useEffect(() => {
      if (prevOpen.current && !open) {
        buttonRef.current?.focus();
      }
      prevOpen.current = open;
    }, [open]);

    const selectIndex = useCallback(
      (index: number) => {
        const opt = filteredOptions[index];
        if (!opt || isOptionDisabled(opt)) return;
        onChange(opt.value);
        setOpen(false);
      },
      [onChange, filteredOptions]
    );

    const onListKeyDown = useCallback(
      (e: KeyboardEvent<HTMLDivElement | HTMLInputElement>) => {
        if (filteredOptions.length === 0) return;
        switch (e.key) {
          case "ArrowDown": {
            e.preventDefault();
            setHighlightedIndex((i) => {
              let next = i + 1;
              while (
                next < filteredOptions.length &&
                isOptionDisabled(filteredOptions[next])
              ) {
                next += 1;
              }
              return next < filteredOptions.length ? next : i;
            });
            break;
          }
          case "ArrowUp": {
            e.preventDefault();
            setHighlightedIndex((i) => {
              let prev = i - 1;
              while (prev >= 0 && isOptionDisabled(filteredOptions[prev])) {
                prev -= 1;
              }
              return prev >= 0 ? prev : i;
            });
            break;
          }
          case "Home": {
            e.preventDefault();
            const first = filteredOptions.findIndex((o) => !isOptionDisabled(o));
            setHighlightedIndex(first >= 0 ? first : 0);
            break;
          }
          case "End": {
            e.preventDefault();
            let last = -1;
            for (let j = filteredOptions.length - 1; j >= 0; j -= 1) {
              if (!isOptionDisabled(filteredOptions[j])) {
                last = j;
                break;
              }
            }
            setHighlightedIndex(last >= 0 ? last : 0);
            break;
          }
          case "Enter": {
            e.preventDefault();
            selectIndex(highlightedIndex);
            break;
          }
          case "Escape": {
            e.preventDefault();
            setOpen(false);
            break;
          }
          default:
            break;
        }
      },
      [highlightedIndex, filteredOptions, selectIndex]
    );

    const onTriggerKeyDown = useCallback(
      (e: KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          if (!open) {
            setOpen(true);
          }
        }
      },
      [disabled, open]
    );

    const showClear = isClearable && !disabled && value !== "";

    const resolvedPanelMinWidth = useMemo(() => {
      if (panelMinWidth != null) return panelMinWidth;
      const labels = [
        placeholder,
        ...options.map((o) => o.label),
      ];
      const longest = labels.reduce(
        (max, label) => (label.length > max ? label.length : max),
        0,
      );
      return Math.min(480, Math.max(200, Math.ceil(longest * 7.5) + 48));
    }, [panelMinWidth, options, placeholder]);

    const onClear = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onChange("");
        setOpen(false);
      },
      [onChange]
    );

    const inline = labelLayout === "inline";
    const compact = density === "compact";

    const dropdown = (
      <Dropdown
        id={rootId}
        className={styles.dropdownRoot}
        open={open}
        panelClassName={clsx(styles.selectPanel, compact && styles.selectPanelCompact)}
        panelRef={panelRef}
        panelMinWidth={resolvedPanelMinWidth}
        trigger={
          <button
            ref={buttonRef}
            id={triggerId}
            type="button"
            disabled={disabled}
            className={clsx(
              styles.trigger,
              compact && styles.triggerCompact,
              hasError && styles.triggerError,
            )}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-invalid={hasError ? true : undefined}
            aria-describedby={hasError ? errorId : undefined}
            onClick={() => !disabled && setOpen((o) => !o)}
            onKeyDown={onTriggerKeyDown}
          >
            <span
              title={selected?.label}
              className={clsx(
                styles.triggerLabel,
                compact ? styles.triggerLabelCompact : "body-sm",
                !selected && styles.triggerLabelPlaceholder,
                !selected && (compact ? "sub-heading-xs" : "sub-heading-sm"),
              )}
            >
              {selected ? (
                <span className={styles.optionContent}>
                  {selected.icon != null ? <span className={styles.optionIcon}>{selected.icon}</span> : null}
                  {selected.label}
                </span>
              ) : placeholder}
            </span>
            {showClear ? (
              <button
                type="button"
                tabIndex={-1}
                className={styles.clearBtn}
                aria-label="Clear selection"
                onClick={onClear}
                onMouseDown={(e) => e.preventDefault()}
              >
                <X size={14} strokeWidth={2} aria-hidden />
              </button>
            ) : null}
            <ChevronDown
              className={clsx(styles.chevron, open && styles.chevronOpen)}
              aria-hidden
              size={compact ? 14 : 18}
              strokeWidth={2}
            />
          </button>
        }
      >
        {searchable && (
          <input
            ref={searchRef}
            type="text"
            className={styles.searchInput}
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setHighlightedIndex(0);
            }}
            onKeyDown={onListKeyDown}
            aria-label="Search options"
          />
        )}
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          tabIndex={-1}
          className={clsx(
            styles.listbox,
            compact ? styles.listboxCompact : "body-sm",
          )}
          aria-activedescendant={
            open && filteredOptions[highlightedIndex]
              ? `${listboxId}-opt-${highlightedIndex}`
              : undefined
          }
          onKeyDown={onListKeyDown}
        >
          {filteredOptions.length === 0 ? (
            <div className={clsx(styles.option, styles.noResults)}>
              No results found
            </div>
          ) : (
            filteredOptions.map((opt, index) => (
              <div
                key={opt.value}
                id={`${listboxId}-opt-${index}`}
                role="option"
                aria-selected={value === opt.value}
                aria-disabled={opt.disabled ? true : undefined}
                className={clsx(
                  styles.option,
                  opt.disabled && styles.optionDisabled,
                )}
                data-highlighted={index === highlightedIndex ? "true" : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (!opt.disabled) selectIndex(index);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <span className={styles.optionContent}>
                  {opt.icon != null ? <span className={styles.optionIcon}>{opt.icon}</span> : null}
                  {opt.label}
                </span>
              </div>
            ))
          )}
        </div>
      </Dropdown>
    );

    return (
      <div
        ref={setWrapperRef}
        className={clsx(styles.wrapper, inline && styles.wrapperInline)}
      >
        {label ? (
          <label
            className={clsx("sub-heading-md", styles.label, inline && styles.labelInline)}
            htmlFor={triggerId}
          >
            {label}
            {required ? (
              <span className="text-danger text-nowrap" aria-hidden>
                {"\u00A0*"}
              </span>
            ) : null}
          </label>
        ) : null}
        <div
          className={clsx(
            inline ? styles.control : styles.controlPassThrough,
          )}
        >
          {dropdown}
          {hasError ? (
            <p
              id={errorId}
              className={clsx(styles.message, styles.messageError)}
              role="alert"
            >
              {error}
            </p>
          ) : null}
          <input
            ref={constraintValidationInputRef}
            type="text"
            className={styles.constraintValidationInput}
            tabIndex={-1}
            aria-hidden
            required={required && !disabled}
            value={value !== "" ? "selected" : ""}
            onChange={() => { }}
          />
        </div>
      </div>
    );
  }
);
